import { useState } from "react";
import {
  usePlayMutations,
  type ReplanTriggerKind,
} from "../../hooks/usePlay";

interface RequestReplanModalProps {
  workspaceId: string;
  playId: string;
  onClose: () => void;
}

const TRIGGER_OPTIONS: { value: ReplanTriggerKind; label: string }[] = [
  { value: "agent_failure", label: "Agent failure" },
  { value: "stakeholder_change", label: "Stakeholder change" },
  { value: "external_blocker", label: "External blocker" },
  { value: "cxo_directed", label: "CXO directed" },
];

export function RequestReplanModal({
  workspaceId,
  playId,
  onClose,
}: RequestReplanModalProps) {
  const m = usePlayMutations(workspaceId, playId);
  const [trigger, setTrigger] = useState<ReplanTriggerKind>("cxo_directed");
  const [rationale, setRationale] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!rationale.trim()) {
      setError("Rationale is required");
      return;
    }
    try {
      await m.requestReplan.mutateAsync({
        trigger_kind: trigger,
        rationale: rationale.trim(),
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div
      data-testid="request-replan-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: 24,
          width: "min(520px, 92vw)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
        }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          Request a replan
        </h3>
        <p
          style={{
            margin: "8px 0 16px",
            fontSize: 13,
            color: "var(--text-3)",
          }}
        >
          Replans pause the play until a new graph lands and you sign off.
        </p>
        <div style={{ display: "grid", gap: 12 }}>
          <label>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-3)",
                letterSpacing: ".14em",
              }}
            >
              TRIGGER KIND
            </span>
            <select
              data-testid="replan-trigger"
              value={trigger}
              onChange={(e) => setTrigger(e.target.value as ReplanTriggerKind)}
              style={{
                marginTop: 4,
                width: "100%",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 8,
              }}
            >
              {TRIGGER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-3)",
                letterSpacing: ".14em",
              }}
            >
              RATIONALE
            </span>
            <textarea
              data-testid="replan-rationale-input"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={4}
              style={{
                marginTop: 4,
                width: "100%",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 8,
                fontFamily: "inherit",
              }}
            />
          </label>
        </div>
        {error ? (
          <div
            data-testid="replan-modal-error"
            style={{ color: "var(--danger)", fontSize: 12, marginTop: 8 }}
          >
            {error}
          </div>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 16,
          }}
        >
          <button
            type="button"
            className="btn"
            onClick={onClose}
            data-testid="replan-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={submit}
            disabled={m.requestReplan.isPending}
            data-testid="replan-modal-submit"
          >
            Request replan
          </button>
        </div>
      </div>
    </div>
  );
}
